/**
 * حالتی که رفرشِ صفحه نمی‌پراندش — در خودِ آدرس.
 *
 * ═══ چرا آدرس و نه `localStorage` ═══
 *
 * هر دو رفرش را رد می‌کنند، ولی آدرس دو چیزِ دیگر هم می‌دهد که این صفحه
 * واقعاً لازمشان دارد:
 *
 *   • **قابلِ فرستادن است.** «این ردیف را ببین» می‌شود یک لینک، نه یک
 *     دستورالعملِ سه‌مرحله‌ای.
 *   • **دو تبِ مرورگر با هم قاطی نمی‌شوند.** با `localStorage`، باز کردنِ
 *     سربرگِ «منابع» در یک پنجره فیلترِ پنجره‌ی دیگر را هم عوض می‌کند —
 *     دقیقاً وقتی که آدم دو تا باز کرده تا مقایسه کند.
 *
 * ⚠️ **`replaceState` و نه `pushState`.** عوض کردنِ یک فیلتر نباید یک
 * ردیفِ تازه در تاریخچه‌ی مرورگر بسازد؛ وگرنه بعد از ده بار تایپ در
 * جعبه‌ی جستجو، دکمه‌ی «برگرد» ده بار باید زده شود تا از صفحه بیرون
 * بیایی.
 *
 * ⚠️ **مقدارِ پیش‌فرض از آدرس حذف می‌شود، نه اینکه `?tab=crawler` بنویسد.**
 * آدرسِ تمیز یعنی وقتی چیزی غیرعادی در آن هست، واقعاً به چشم می‌آید.
 */
import { useCallback, useState } from 'react';

function read(key: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(key);
}

function write(key: string, value: string, fallback: string): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (value === fallback || value === '') params.delete(key);
  else params.set(key, value);
  const query = params.toString();
  window.history.replaceState(
    null, '', `${window.location.pathname}${query ? `?${query}` : ''}`,
  );
}

/**
 * یک رشته در آدرس. `allowed` که بدهی، مقدارِ ناشناخته به `fallback`
 * برمی‌گردد — تا یک آدرسِ دستکاری‌شده صفحه‌ی خالی نسازد.
 */
export function useUrlState(
  key: string,
  fallback: string,
  allowed?: readonly string[],
): [string, (value: string) => void] {
  const [value, setValue] = useState<string>(() => {
    const found = read(key);
    if (found == null) return fallback;
    if (allowed && !allowed.includes(found)) return fallback;
    return found;
  });

  const set = useCallback((next: string) => {
    setValue(next);
    write(key, next, fallback);
  }, [key, fallback]);

  return [value, set];
}

/**
 * همان، برای فهرست — با کاما در آدرس (`?stage=new,inspected`).
 *
 * ⚠️ کاما و نه JSON: این آدرس قرار است خوانده و دستی دستکاری شود.
 */
export function useUrlList(
  key: string,
  allowed?: readonly string[],
): [string[], (value: string[]) => void] {
  const [raw, setRaw] = useUrlState(key, '');
  const list = raw ? raw.split(',').filter((v) => !allowed || allowed.includes(v)) : [];
  return [list, (next: string[]) => setRaw(next.join(','))];
}
